import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Button, Grid, TextField, Select, MenuItem,
  FormControl, InputLabel, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@material-ui/core'
import { Edit as EditIcon, Warning as WarningIcon } from '@material-ui/icons'

const FollowUpPanel = (props) => {
  const { projectId, project, url, users, onRefresh } = props
  const fu = project?.followUp || {}
  const [editing, setEditing] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [losing, setLosing] = React.useState(false)
  const [lostReason, setLostReason] = React.useState('')
  const [winning, setWinning] = React.useState(false)

  // Edit form state
  const [presenter, setPresenter] = React.useState(fu.presenter || '')
  const [presenterName, setPresenterName] = React.useState(fu.presenterName || '')
  const [followUpOwner, setFollowUpOwner] = React.useState(fu.followUpOwner || '')
  const [followUpName, setFollowUpName] = React.useState(fu.followUpName || '')
  const [expectedCloseDate, setExpectedCloseDate] = React.useState(fu.expectedCloseDate ? fu.expectedCloseDate.slice(0, 10) : '')
  const [cadenceDays, setCadenceDays] = React.useState(fu.cadenceDays || 7)
  const [repricingDays, setRepricingDays] = React.useState(fu.repricingThresholdDays || 45)

  const isSubmitted = project?.status === 'submitted'
  const isReview = project?.status === 'review'
  const isWon = project?.status === 'won'
  const isLost = project?.status === 'lost'

  const daysSinceSubmission = fu.submittedAt
    ? Math.floor((Date.now() - new Date(fu.submittedAt).getTime()) / 86400000)
    : 0
  const isStale = isSubmitted && fu.submittedAt && daysSinceSubmission >= (fu.repricingThresholdDays || 45)

  const handleSaveFollowUp = () => {
    setSaving(true)
    axios.put(`${url}/api/bid-estimation/projects/${projectId}/follow-up`, {
      presenter, presenterName, followUpOwner, followUpName,
      expectedCloseDate: expectedCloseDate || null,
      cadenceDays, repricingThresholdDays: repricingDays
    }).then(() => {
      setSaving(false)
      setEditing(false)
      onRefresh()
    }).catch(err => { console.error(err); setSaving(false) })
  }

  const handleUserSelect = (setter, nameSetter) => (e) => {
    const userId = e.target.value
    const user = (users || []).find(u => u._id === userId)
    setter(userId)
    nameSetter(user?.name || '')
  }

  const handleWin = () => {
    setWinning(true)
    axios.post(`${url}/api/bid-estimation/projects/${projectId}/close/win`).then(() => {
      setWinning(false)
      onRefresh()
    }).catch(err => { console.error(err); setWinning(false) })
  }

  const handleLoss = () => {
    axios.post(`${url}/api/bid-estimation/projects/${projectId}/close/loss`, { lostReason }).then(() => {
      setLosing(false)
      setLostReason('')
      onRefresh()
    }).catch(err => { console.error(err); setLosing(false) })
  }

  const userList = users || []

  return (
    <div className="hmi-panel">
      <div className="hmi-panel-body pad">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>Follow-Up</h4>
        {(isSubmitted || isReview) && (
          <IconButton size="small" onClick={() => setEditing(!editing)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      {/* Info grid */}
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <div className="eyebrow" style={{ marginBottom: 2 }}>Presenter</div>
          <div style={{ fontSize: 13 }}>{fu.presenterName || 'Not set'}</div>
        </Grid>
        <Grid item xs={6}>
          <div className="eyebrow" style={{ marginBottom: 2 }}>Follow-up Owner</div>
          <div style={{ fontSize: 13 }}>{fu.followUpName || 'Not set'}</div>
        </Grid>
        <Grid item xs={6}>
          <div className="eyebrow" style={{ marginBottom: 2 }}>Expected Close</div>
          <div style={{ fontSize: 13 }}>
            {fu.expectedCloseDate ? new Date(fu.expectedCloseDate).toLocaleDateString() : 'Not set'}
          </div>
        </Grid>
        <Grid item xs={6}>
          <div className="eyebrow" style={{ marginBottom: 2 }}>Cadence</div>
          <div style={{ fontSize: 13 }}>Every {fu.cadenceDays || 7} days</div>
        </Grid>
      </Grid>

      {/* Stale warning */}
      {isStale && (
        <div style={{ backgroundColor: 'var(--warn-tint)', padding: '8px 12px', marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, borderRadius: 6 }}>
          <WarningIcon style={{ color: 'var(--warn)', fontSize: 18 }} />
          <div style={{ fontSize: 12 }}>
            Estimate is {daysSinceSubmission} days old — consider repricing before close.
          </div>
        </div>
      )}

      {/* Win/Loss buttons */}
      {isSubmitted && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button size="small" variant="contained" onClick={handleWin} disabled={winning}
            style={{ backgroundColor: 'var(--brand)', color: '#fff', textTransform: 'none', fontSize: 12 }}>
            {winning ? 'Marking...' : 'Mark Won'}
          </Button>
          <Button size="small" variant="outlined" onClick={() => setLosing(true)}
            style={{ borderColor: 'var(--risk)', color: 'var(--risk)', textTransform: 'none', fontSize: 12 }}>
            Mark Lost
          </Button>
        </div>
      )}

      {/* Won/Lost banner */}
      {isWon && fu.wonAt && (
        <div style={{ backgroundColor: 'var(--ok-tint)', padding: '8px 12px', marginTop: 8, borderRadius: 6 }}>
          <div style={{ color: 'var(--brand)', fontSize: 12 }}>
            Won on {new Date(fu.wonAt).toLocaleDateString()}
          </div>
        </div>
      )}
      {isLost && fu.lostAt && (
        <div style={{ backgroundColor: 'var(--risk-tint)', padding: '8px 12px', marginTop: 8, borderRadius: 6 }}>
          <div style={{ color: 'var(--risk)', fontSize: 12 }}>
            Lost on {new Date(fu.lostAt).toLocaleDateString()}
            {fu.lostReason && ` \u2014 ${fu.lostReason}`}
          </div>
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #eee' }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Presenter</InputLabel>
                <Select value={presenter} onChange={handleUserSelect(setPresenter, setPresenterName)} label="Presenter">
                  <MenuItem value="">None</MenuItem>
                  {userList.map(u => <MenuItem key={u._id} value={u._id}>{u.name || u._id}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Follow-up Owner</InputLabel>
                <Select value={followUpOwner} onChange={handleUserSelect(setFollowUpOwner, setFollowUpName)} label="Follow-up Owner">
                  <MenuItem value="">None</MenuItem>
                  {userList.map(u => <MenuItem key={u._id} value={u._id}>{u.name || u._id}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" variant="outlined" label="Expected Close" type="date"
                value={expectedCloseDate} onChange={e => setExpectedCloseDate(e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" variant="outlined" label="Cadence (days)" type="number"
                value={cadenceDays} onChange={e => setCadenceDays(parseInt(e.target.value) || 7)} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" variant="outlined" label="Reprice (days)" type="number"
                value={repricingDays} onChange={e => setRepricingDays(parseInt(e.target.value) || 45)} />
            </Grid>
          </Grid>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Button size="small" variant="contained" color="primary" onClick={handleSaveFollowUp} disabled={saving}
              style={{ textTransform: 'none', fontSize: 12 }}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button size="small" onClick={() => setEditing(false)} style={{ textTransform: 'none', fontSize: 12 }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      </div>{/* end hmi-panel-body */}

      {/* Loss reason dialog */}
      <Dialog open={losing} onClose={() => setLosing(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark Project Lost</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Reason for loss" multiline rows={3} variant="outlined"
            value={lostReason} onChange={e => setLostReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLosing(false)}>Cancel</Button>
          <Button onClick={handleLoss} variant="contained" color="secondary">Mark Lost</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  users: state.users || []
})

export default connect(mapStateToProps)(FollowUpPanel)
